import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { HexIcon } from '@/components/ui/hex-icon';
import { useAuth } from '@/contexts/auth-context';
import { useWorkspaceOnboarding } from '@/hooks/use-workspace-onboarding';
import { 
  Zap, 
  User, 
  LogOut, 
  LogIn, 
  Settings, 
  Menu,
  X,
  Users,
  Plus,
  Building
} from 'lucide-react';

interface LandingHeaderProps {
  onContactModalOpen: () => void;
}

export const LandingHeader: React.FC<LandingHeaderProps> = ({ onContactModalOpen }) => {
  const { user, signOut, loading } = useAuth();
  const { needsWorkspace, reopenModal } = useWorkspaceOnboarding();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const SHOW_SIGNUP = import.meta.env.VITE_ENABLE_SIGNUP === 'true';

  const handleSignOut = async () => {
    await signOut();
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const NavLinks = () => (
    <>
      <button 
        onClick={() => scrollToSection('features')}
        className="text-foreground hover:text-primary transition-colors cursor-pointer font-medium"
      >
        Features
      </button>
      <button 
        onClick={() => scrollToSection('pricing')}
        className="text-foreground hover:text-primary transition-colors cursor-pointer font-medium"
      >
        Options
      </button>
      <a 
        href="https://wattfactory.substack.com" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-foreground hover:text-primary transition-colors cursor-pointer font-medium"
      >
        Newsletter
      </a>
    </>
  );

  const AuthSection = () => (
    <>
      {loading ? (
        <div className="w-8 h-8 animate-pulse bg-muted rounded"></div>
      ) : user ? (
        <div className="flex items-center gap-2">
          {needsWorkspace ? (
            <Button 
              variant="default" 
              size="sm" 
              className="gap-1"
              onClick={reopenModal}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Create Workspace</span>
            </Button>
          ) : (
            <Link to="/workspaces">
              <Button variant="default" size="sm" className="gap-1">
                <Building className="w-4 h-4" />
                <span className="hidden sm:inline">My Workspaces</span>
              </Button>
            </Link>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-1"
            onClick={() => signOut()}
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sign Out</span>
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {SHOW_SIGNUP ? (
            <Link to="/auth?mode=signup">
              <Button variant="default" size="sm" className="gap-1">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Sign Up</span>
              </Button>
            </Link>
          ) : (
            <Button 
              variant="default" 
              size="sm" 
              className="gap-1"
              onClick={onContactModalOpen}
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Beta Access</span>
            </Button>
          )}
          <Link to="/auth">
            <Button variant="outline" size="sm" className="gap-1">
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">Sign In</span>
            </Button>
          </Link>
        </div>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-50 border-b bg-card/50 backdrop-blur-sm">
      <div className="hex-container py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <HexIcon icon={Zap} size="small" className="bg-primary/20" />
            <span className="text-xl font-bold text-foreground">WattFactory</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <NavLinks />
            <AuthSection />
          </nav>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col h-full">
                  {/* Mobile Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <HexIcon icon={Zap} size="small" className="bg-primary/20" />
                      <span className="text-xl font-bold text-foreground">WattFactory</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="p-2"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Mobile Navigation */}
                  <nav className="flex flex-col gap-4 mb-6">
                    <button 
                      onClick={() => scrollToSection('features')}
                      className="text-left text-foreground hover:text-primary transition-colors font-medium py-2"
                    >
                      Features
                    </button>
                    <button 
                      onClick={() => scrollToSection('pricing')}
                      className="text-left text-foreground hover:text-primary transition-colors font-medium py-2"
                    >
                      Options
                    </button>
                    <a 
                      href="https://wattfactory.substack.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-left text-foreground hover:text-primary transition-colors font-medium py-2"
                    >
                      Newsletter
                    </a>
                  </nav>

                  {/* Mobile Auth Section */}
                  <div className="mt-auto">
                    {loading ? (
                      <div className="w-full h-10 animate-pulse bg-muted rounded"></div>
                    ) : user ? (
                      <div className="flex flex-col gap-3">
                        {needsWorkspace ? (
                          <Button 
                            variant="default" 
                            onClick={reopenModal}
                            className="gap-2 justify-start"
                          >
                            <Plus className="w-4 h-4" />
                            Create Workspace
                          </Button>
                        ) : (
                          <Link to="/workspaces" className="w-full">
                            <Button variant="default" className="w-full gap-2 justify-start">
                              <Building className="w-4 h-4" />
                              My Workspaces
                            </Button>
                          </Link>
                        )}
                        <Button 
                          variant="outline" 
                          onClick={handleSignOut}
                          className="gap-2 justify-start"
                        >
                          <LogOut className="w-4 h-4" />
                          Sign Out
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {SHOW_SIGNUP ? (
                          <Link to="/auth?mode=signup" className="w-full">
                            <Button variant="default" className="w-full gap-2 justify-start">
                              <Users className="w-4 h-4" />
                              Sign Up
                            </Button>
                          </Link>
                        ) : (
                          <Button 
                            variant="default" 
                            onClick={onContactModalOpen}
                            className="w-full gap-2 justify-start"
                          >
                            <Users className="w-4 h-4" />
                            Beta Access
                          </Button>
                        )}
                        <Link to="/auth" className="w-full">
                          <Button variant="outline" className="w-full gap-2 justify-start">
                            <LogIn className="w-4 h-4" />
                            Sign In
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}; 